import AtriumApi from "api";
import LineChart from "components/charts/LineChart";
import Loader from "components/loader/Loader";
import { useEffect, useMemo, useState } from "react";
import html2canvas from "html2canvas";
import { useRef } from "react";

const chartOptions = {
  chart: {
    id: "predicted-attendance-chart-123",
    type: "line",
    toolbar: {
      show: false,
    },
    dropShadow: {
      enabled: false,
    },
  },
  markers: {
    size: 0,
    colors: "white",
    strokeColors: "#7551FF",
    strokeWidth: 3,
    strokeOpacity: 0.9,
    strokeDashArray: 0,
    fillOpacity: 1,
    // discrete: [],
    shape: "circle",
    radius: 2,
    offsetX: 0,
    offsetY: 0,
    showNullDataPoints: true,
  },
  tooltip: {
    theme: "dark",
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: "smooth",
    type: "line",
    width: [5, 3],
    dashArray: [0, 8],
    colors: ["#EF9C8F", "#CED7EE"],
  },
  xaxis: {
    type: "numeric",

    labels: {
      style: {
        colors: "#A3AED0",
        fontSize: "12px",
        fontWeight: "500",
      },
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  yaxis: {
    type: "numeric",
    labels: {
      style: {
        colors: "#A3AED0",
        fontSize: "12px",
        fontWeight: "500",
      },
    },
  },
  legend: {
    show: true,
    horizontalAlign: "left",
    markers: {
      offsetX: -5,
      strokeWidth: 0,
      size: 5,
    },
    labels: {
      colors: ["#A3AED0", "#A3AED0"],
    },
    itemMargin: {
      horizontal: 15,
    },
  },
  grid: {
    show: false,
  },
};

function PredictedAttendance({ selectedCampusId }) {
  const [filter, setFilter] = useState("weekly");
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(false);
  const exportRef = useRef();

  const newOptions = useMemo(() => {
    const getXAxisCategories = () => {
      if (!trendData?.chartData?.length) return [];

      if (filter === "weekly") {
        return ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
      }

      if (filter === "monthly") {
        // const daysInMonth = new Date().getDate();
        const today = new Date();
        const totalDays = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0
        ).getDate();
        return Array.from({ length: totalDays }, (_, i) => `${i + 1}`);
      }

      if (filter === "yearly") {
        return [
          "JAN",
          "FEB",
          "MAR",
          "APR",
          "MAY",
          "JUN",
          "JUL",
          "AUG",
          "SEP",
          "OCT",
          "NOV",
          "DEC",
        ];
      }

      return [];
    };
    return {
      ...chartOptions,
      colors: ["#EF9C8F", "#CED7EE"],
      xaxis: {
        ...chartOptions.xaxis,
        categories: getXAxisCategories(),
      },
    };
  }, [filter, trendData]);

  // const newOptions = {
  //   ...chartOptions,
  //   colors: ["#EF9C8F", "#CED7EE"],
  //   xaxis: {
  //     ...chartOptions.xaxis,
  //     categories: xAxisCategoriesMap[filter] || xAxisCategoriesMap["weekly"],
  //   },
  // };

  useEffect(() => {
    function buildQueryString() {
      let query = "";
      if (filter && filter !== "all") {
        query += `filter=${filter}`;
      }
      if (selectedCampusId && selectedCampusId !== "all") {
        query += `${query ? "&" : ""}campusId=${selectedCampusId}`;
      }
      return query;
    }

    const getPredictedAttendance = async () => {
      try {
        setLoading(true);
        const query = buildQueryString();
        const response = await AtriumApi.get(
          `/v1/trends${query ? `?${query}` : ""}`
        );
        if (response) {
          setLoading(false);
          setTrendData(response?.data?.data);

          // const incomingChartData = response?.data?.data?.chartData || [];
        }
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };
    getPredictedAttendance();
  }, [filter, selectedCampusId]);

  // display data on week days base
  const dayKey = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
    new Date().getDay()
  ];
  const todayValue = trendData?.summary?.[dayKey];

  // yesterday comparison value and show color red or green
  // const isNegative = trendData?.percentChange?.includes("-");
  const isNegative = String(trendData?.percentageChange || "").includes("-");

  // download the chart PNG

  const handleExportChart = async () => {
    const canvas = await html2canvas(exportRef.current, {
      backgroundColor: "#fff",
      scale: 2,
    });
    const imgData = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.href = imgData;
    link.download = "predicted-attendance.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div
        ref={exportRef}
        className="flex-1 relative items-center rounded-[30px] p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full w-full">
            <Loader />
          </div>
        ) : (
          <>
            {/* header */}
            <div className="flex">
              <div className="flex-1">
                <p className="text-gray-700">Predicted Attendance</p>
                <p>
                  <span className="text-2xl font-bold mr-2">{todayValue}</span>
                  <span
                    className={isNegative ? "arrow-down" : "arrow-up"}
                  ></span>
                  <span
                    className={`text-[12px] font-bold ml-2 ${
                      isNegative ? "text-red-500" : "text-green-atrium"
                    }`}
                  >
                    {trendData?.percentChange}
                  </span>
                </p>
              </div>
              <div>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="bg-[transparent] h-12 text-sm text-gray-700 rounded-xl pr-4 outline-none rounded-2xl p-[8px] mr-4"
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <button
                  className="rounded-2xl bg-gray-50 dark:!bg-navy-700 p-[12px] pl-6 pr-6"
                  onClick={handleExportChart}
                >
                  <span className="align-middle inline-block mr-2">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M17.5 12.5V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V12.5M14.1667 6.66667L10 2.5M10 2.5L5.83333 6.66667M10 2.5V12.5"
                        stroke="#707EAE"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span>Export</span>
                </button>
              </div>
            </div>

            {/* chart */}
            <div className="block h-[300px]">
              <div className="h-full w-full">
                {/* <LineChart chartData={chartData} chartOptions={newOptions} /> */}
                {trendData?.chartData?.length > 0 ? (
                  <LineChart
                    chartData={trendData?.chartData}
                    chartOptions={newOptions}
                    chartId="predicted-attendance-chart-123"
                  />
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default PredictedAttendance;
